import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  query,
  orderBy,
  doc,
  getDoc,
  setDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  where,
  serverTimestamp,
} from "firebase/firestore";
import { db, auth } from "../firebase";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import { useUser } from "../components/context/UserContext";
import CustomTable from "../templates/CustomTable";
import { familyHeader } from "../utils/HeaderData";
import {
  Church,
  Article,
  Collections,
  Person,
  Group,
  Groups,
  MenuBook,
  TrendingUp,
  ArrowForward,
  Star,
  ChevronLeft,
  ChevronRight,
  Visibility,
  Search,
  MergeType,
} from "@mui/icons-material";

const PAGE_SIZE = 10;
const TWO_MONTHS_MS = 60 * 24 * 60 * 60 * 1000;
const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

const isRecentlySelected = (memberId, history) =>
  history.some(
    (h) => h.memberId === memberId && Date.now() - new Date(h.setAt).getTime() < TWO_MONTHS_MS,
  );

const StatCard = ({ label, count, icon, color, bg, path, navigate, onClick }) => (
  <div
    className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 flex items-center gap-3 cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all"
    onClick={() => (onClick ? onClick() : navigate(path))}
  >
    <div
      className="rounded-xl p-2.5 flex-shrink-0"
      style={{ backgroundColor: bg }}
    >
      <span style={{ color, display: "block", lineHeight: 1, fontSize: 18 }}>
        {icon}
      </span>
    </div>
    <div className="flex-1 min-w-0">
      <div
        className="text-xl font-bold font-aref leading-tight"
        style={{ color }}
      >
        {count}
      </div>
      <div className="text-gray-400 text-xs font-abel mt-0.5 truncate">
        {label}
      </div>
    </div>
    <ArrowForward style={{ color: "#d1d5db", fontSize: 14 }} />
  </div>
);

const Home = () => {
  const navigate = useNavigate();
  const { userRole } = useUser();

  const [counts, setCounts] = useState({
    events: 0,
    articles: 0,
    gallery: 0,
    authors: 0,
    members: 0,
    tracts: 0,
  });
  const [topArticle, setTopArticle] = useState(null);

  const [members, setMembers] = useState([]);
  const [faceOfWeek, setFaceOfWeek] = useState(null);
  const [fotwHistory, setFotwHistory] = useState([]);
  const [fotwSetAt, setFotwSetAt] = useState(null);
  const [fotwLoading, setFotwLoading] = useState(false);
  const [fotwModalOpen, setFotwModalOpen] = useState(false);
  const [fotwPage, setFotwPage] = useState(0);

  const [families, setFamilies] = useState([]);
  const [familySearchQuery, setFamilySearchQuery] = useState("");
  const [selectedFamily, setSelectedFamily] = useState(null);
  const [familyMembersOpen, setFamilyMembersOpen] = useState(false);
  const [mergeModalOpen, setMergeModalOpen] = useState(false);
  const [mergingGroupKey, setMergingGroupKey] = useState(null);

  const formatDob = (dob) => {
    if (!dob) return "";
    if (dob.includes("-") && dob.length === 10) {
      const [, month, day] = dob.split("-");
      const months = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      return `${parseInt(day)} ${months[parseInt(month) - 1]}`;
    }
    return dob;
  };

  const formatDept = (dept) =>
    Array.isArray(dept) ? dept.join(", ") : dept || "";

  const fetchCounts = async () => {
    try {
      const [evSnap, arSnap, gaSnap, auSnap, meSnap, trSnap, faSnap] =
        await Promise.all([
          getDocs(collection(db, "events")),
          getDocs(collection(db, "articles")),
          getDocs(collection(db, "gallery")),
          getDocs(collection(db, "authors")),
          getDocs(collection(db, "members")),
          getDocs(collection(db, "sbc-tracts")),
          getDocs(collection(db, "families")),
        ]);
      setCounts({
        events: evSnap.size,
        articles: arSnap.size,
        gallery: gaSnap.size,
        authors: auSnap.size,
        members: meSnap.size,
        tracts: trSnap.size,
      });
      setFamilies(
        faSnap.docs.map((d) => ({
          _id: d.id,
          ...d.data(),
          createdAt: d.data().createdAt?.toDate() || new Date(),
        }))
      );
    } catch (err) {
      console.error(err);
    }
  };

  const fetchFaceOfWeek = async () => {
    try {
      const twoMonthsAgo = new Date(Date.now() - TWO_MONTHS_MS).toISOString();
      const [fotwSnap, membersSnap, historySnap] = await Promise.all([
        getDoc(doc(db, "settings", "faceOfWeek")),
        getDocs(query(collection(db, "members"), orderBy("createdAt", "desc"))),
        getDocs(
          query(
            collection(db, "faceoftheweek_history"),
            where("setAt", ">", twoMonthsAgo),
            orderBy("setAt", "desc"),
          ),
        ),
      ]);
      const allMembers = membersSnap.docs.map((d) => ({
        _id: d.id,
        ...d.data(),
      }));
      const approvedMembers = allMembers.filter((m) => m.status === "approved");
      const historyList = historySnap.docs.map((d) => d.data());

      // Modal only lists approved members; FOTW card searches all so the selected member always shows
      setMembers(approvedMembers);
      setFotwHistory(historyList);

      let current = null;
      let currentSetAt = null;
      if (fotwSnap.exists()) {
        currentSetAt = fotwSnap.data().setAt || null;
        current = allMembers.find((m) => m._id === fotwSnap.data().memberId) || null;
        if (current) setFaceOfWeek(current);
      }
      setFotwSetAt(currentSetAt);

      const needsRotation = !currentSetAt || Date.now() - new Date(currentSetAt).getTime() >= WEEK_MS;
      if (needsRotation) {
        const eligible = approvedMembers.filter((m) => !isRecentlySelected(m._id, historyList));
        if (eligible.length > 0) {
          const pick = eligible[Math.floor(Math.random() * eligible.length)];
          await saveFaceOfWeek(pick, { auto: true });
        } else {
          console.warn(
            "Vessel of the Week: no eligible member for auto-rotation — all approved members were selected within the last 2 months.",
          );
        }
      }
    } catch (err) {
      console.error(err);
    }
  };

  const fetchTopArticle = async () => {
    try {
      const [articlesSnap, commentsSnap] = await Promise.all([
        getDocs(collection(db, "articles")),
        getDocs(collection(db, "comments")),
      ]);

      // Count comments per articleId
      const countMap = {};
      commentsSnap.docs.forEach((d) => {
        const aid = d.data().articleId;
        if (aid) countMap[aid] = (countMap[aid] || 0) + 1;
      });

      // Find article with most comments
      let best = null;
      let bestCount = 0;
      articlesSnap.docs.forEach((d) => {
        const c = countMap[d.id] || 0;
        if (c > bestCount) {
          bestCount = c;
          best = { _id: d.id, ...d.data(), commentCount: c };
        }
      });

      // Fall back to most recent article if no comments exist
      if (!best && !articlesSnap.empty) {
        const sorted = articlesSnap.docs.sort(
          (a, b) =>
            (b.data().createdAt?.seconds || 0) -
            (a.data().createdAt?.seconds || 0),
        );
        best = { _id: sorted[0].id, ...sorted[0].data(), commentCount: 0 };
      }

      if (best) setTopArticle(best);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (!auth.currentUser) {
      navigate("/");
      return;
    }
    fetchCounts();
    fetchTopArticle();
    fetchFaceOfWeek();
  }, [navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveFaceOfWeek = async (member, { auto = false } = {}) => {
    const now = new Date().toISOString();

    await Promise.all([
      setDoc(doc(db, "settings", "faceOfWeek"), {
        memberId: member._id,
        memberName: member.name,
        setAt: now,
      }),
      addDoc(collection(db, "faceoftheweek_history"), {
        memberId: member._id,
        memberName: member.name,
        setAt: now,
        createdAt: serverTimestamp(),
      }),
    ]);

    setFaceOfWeek(member);
    setFotwHistory((prev) => [{ memberId: member._id, setAt: now }, ...prev]);
    setFotwSetAt(now);
    toast(
      auto
        ? `🎉 ${member.name} was auto-selected as this week's Vessel of the Week!`
        : `${member.name} set as Vessel of the Week!`,
      { type: "success" },
    );
  };

  const handleSetFaceOfWeek = async (member) => {
    if (isRecentlySelected(member._id, fotwHistory)) return;
    setFotwLoading(member._id);
    try {
      await saveFaceOfWeek(member, { auto: false });
      setFotwModalOpen(false);
      setFotwPage(0);
    } catch (err) {
      toast("Failed to update Vessel of the Week", { type: "error" });
    } finally {
      setFotwLoading(false);
    }
  };

  const totalPages = Math.ceil(members.length / PAGE_SIZE);
  const pagedMembers = members.slice(
    fotwPage * PAGE_SIZE,
    (fotwPage + 1) * PAGE_SIZE,
  );

  const filteredFamilies = families.filter((f) =>
    (f.familyName || "").toLowerCase().includes(familySearchQuery.trim().toLowerCase())
  );

  const duplicateFamilyGroups = useMemo(() => {
    const groups = {};
    families.forEach((f) => {
      const key = (f.familyName || "").trim().toLowerCase();
      if (!key) return;
      if (!groups[key]) groups[key] = [];
      groups[key].push(f);
    });
    return Object.values(groups).filter((g) => g.length > 1);
  }, [families]);

  const sortForMerge = (group) =>
    [...group].sort((a, b) => {
      const countA = (a.members || []).length;
      const countB = (b.members || []).length;
      if (countB !== countA) return countB - countA;
      return (a.createdAt?.getTime?.() || 0) - (b.createdAt?.getTime?.() || 0);
    });

  const handleMergeGroup = async (group) => {
    const groupKey = (group[0].familyName || "").trim().toLowerCase();
    setMergingGroupKey(groupKey);
    try {
      const [survivor, ...toMerge] = sortForMerge(group);
      let mergedMembers = [...(survivor.members || [])];

      for (const fam of toMerge) {
        const famMembers = fam.members || [];
        mergedMembers = [...mergedMembers, ...famMembers];
        await Promise.all(
          famMembers.map((m) => updateDoc(doc(db, "members", m.id), { familyId: survivor._id }))
        );
        await deleteDoc(doc(db, "families", fam._id));
      }

      await updateDoc(doc(db, "families", survivor._id), {
        members: mergedMembers,
        familyCount: mergedMembers.length,
      });

      const userName = auth.currentUser?.email || "Admin";
      await addDoc(collection(db, "notifications"), {
        name: userName,
        message: `Merged duplicate "${survivor.familyName}" family records`,
        createdAt: serverTimestamp(),
      });

      toast(`Merged ${toMerge.length + 1} "${survivor.familyName}" family records into one`, { type: "success" });
      fetchCounts();
    } catch (err) {
      toast("Failed to merge families: " + err.message, { type: "error" });
    } finally {
      setMergingGroupKey(null);
    }
  };

  const stats = [
    {
      label: "Total Events",
      count: counts.events,
      icon: <Church />,
      color: "#000C66",
      bg: "#eef2ff",
      path: "/event",
    },
    {
      label: "Total Articles",
      count: counts.articles,
      icon: <Article />,
      color: "#dc2626",
      bg: "#fef2f2",
      path: "/article",
    },
    {
      label: "Gallery Images",
      count: counts.gallery,
      icon: <Collections />,
      color: "#7c3aed",
      bg: "#f5f3ff",
      path: "/gallery",
    },
    {
      label: "Authors",
      count: counts.authors,
      icon: <Person />,
      color: "#059669",
      bg: "#ecfdf5",
      path: "/author",
    },
    {
      label: "Members",
      count: counts.members,
      icon: <Group />,
      color: "#0891b2",
      bg: "#ecfeff",
      path: "/members",
    },
    {
      label: "SBC Tracts",
      count: counts.tracts,
      icon: <MenuBook />,
      color: "#d97706",
      bg: "#fffbeb",
      path: "/sbc-tracts",
    },
  ];

  return (
    <div className="p-3 sm:p-6 min-h-screen bg-[#f5f7fe]">
      <div className="mb-6 flex items-center gap-4">
        <img
          src="https://rentnowltd.s3.us-east-2.amazonaws.com/salvation/salv_logo.png"
          alt="Salvation Logo"
          className="w-14 h-14 rounded-full object-cover border-2 border-[#000C66]/10 shadow-sm flex-shrink-0"
        />
        <div>
          <h1 className="text-3xl font-bold text-[#000C66] font-aref">
            Dashboard
          </h1>
          <p className="text-gray-400 text-sm font-abel mt-0.5">
            Overview of your ministry's content and community
          </p>
        </div>
      </div>

      {/* Top row: stats (8) + FOTW (4) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 mb-5">
        {/* Stats col-8 */}
        <div className="lg:col-span-8">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {stats.map((s, i) => (
              <StatCard key={i} {...s} navigate={navigate} />
            ))}
          </div>

          {/* Bottom: top article + quick actions */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-5">
            {topArticle && (
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
                <div className="flex items-center gap-2 mb-4">
                  <TrendingUp style={{ color: "#000C66", fontSize: 18 }} />
                  <h2 className="text-sm font-bold text-[#000C66] font-aref">
                    Most Engaged Article
                  </h2>
                </div>
                <div className="flex items-start gap-3">
                  {topArticle.imgSrc || topArticle.imageSrc ? (
                    <img
                      src={topArticle.imgSrc || topArticle.imageSrc}
                      alt={topArticle.title}
                      className="w-14 h-14 rounded-xl object-cover flex-shrink-0 border border-gray-100"
                    />
                  ) : (
                    <div className="w-14 h-14 rounded-xl bg-[#eef2ff] flex items-center justify-center flex-shrink-0">
                      <Article style={{ color: "#000C66", fontSize: 20 }} />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-800 font-aref text-sm leading-snug line-clamp-2">
                      {topArticle.title}
                    </p>
                    <p className="text-gray-400 text-xs font-abel mt-1">
                      by {topArticle.author || "Unknown"}
                    </p>
                    <div className="flex items-center gap-1 mt-1.5">
                      <span className="text-xl font-bold text-[#FFC000] font-aref">
                        {topArticle.commentCount ?? 0}
                      </span>
                      <span className="text-gray-400 text-xs font-abel">
                        interactions
                      </span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => navigate(`/update-article/${topArticle._id}`)}
                  className="mt-3 text-[#000C66] text-xs font-abel font-semibold flex items-center gap-1 hover:gap-2 transition-all"
                >
                  View Article <ArrowForward style={{ fontSize: 13 }} />
                </button>
              </div>
            )}

            {(() => {
              const allActions = [
                {
                  label: "New Event",
                  path: "/create-event",
                  color: "#000C66",
                  roles: ["editor", "admin", "superadmin"],
                },
                {
                  label: "New Article",
                  path: "/create-article",
                  color: "#dc2626",
                  roles: ["editor", "admin", "superadmin"],
                },
                {
                  label: "Add Author",
                  path: "/create-author",
                  color: "#059669",
                  roles: ["editor", "admin", "superadmin"],
                },
                {
                  label: "Add Member",
                  path: "/create-member",
                  color: "#0891b2",
                  roles: ["editor", "admin", "superadmin"],
                },
                {
                  label: "Add Gallery",
                  path: "/create-gallery",
                  color: "#7c3aed",
                  roles: ["admin", "superadmin"],
                },
                {
                  label: "Add Tract",
                  path: "/create-tract",
                  color: "#d97706",
                  roles: ["admin", "superadmin"],
                },
              ];
              const visible = allActions.filter((a) =>
                a.roles.includes(userRole),
              );
              if (!visible.length) return null;
              return (
                <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
                  <h2 className="text-sm font-bold text-[#000C66] font-aref mb-3">
                    Quick Actions
                  </h2>
                  <div className="grid grid-cols-2 gap-2">
                    {visible.map((a, i) => (
                      <button
                        key={i}
                        onClick={() => navigate(a.path)}
                        className="py-2.5 px-3 rounded-xl border border-gray-100 text-left hover:shadow-sm transition font-abel text-xs font-semibold hover:border-gray-200"
                        style={{ color: a.color }}
                      >
                        + {a.label}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })()}
          </div>
        </div>

        {/* FOTW col-4 */}
        <div className="lg:col-span-4">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden h-full flex flex-col">
            <div className="px-5 pt-4 pb-3 border-b border-gray-50 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-2">
                <Star style={{ color: "#FFC000", fontSize: 18 }} />
                <h2 className="text-sm font-bold text-[#000C66] font-aref">
                  Vessel of the Week
                </h2>
              </div>
              {faceOfWeek && (
                <span className="text-xs font-abel font-semibold px-2 py-0.5 bg-amber-50 text-amber-600 border border-amber-200 rounded-full">
                  Active
                </span>
              )}
            </div>

            <div className="p-5 flex flex-col flex-1">
              {faceOfWeek ? (
                <>
                  {/* Photo centered */}
                  <div className="flex justify-center mb-4">
                    {faceOfWeek.imgSrc ? (
                      <img
                        src={faceOfWeek.imgSrc}
                        alt={faceOfWeek.name}
                        className="w-24 h-24 rounded-2xl object-cover border-2 border-amber-300 shadow-md"
                      />
                    ) : (
                      <div className="w-24 h-24 rounded-2xl bg-amber-50 border-2 border-amber-200 flex items-center justify-center">
                        <Star style={{ color: "#FFC000", fontSize: 32 }} />
                      </div>
                    )}
                  </div>

                  <p className="text-base font-bold text-[#000C66] font-aref text-center">
                    {faceOfWeek.name}
                  </p>
                  {fotwSetAt && (
                    <p className="text-[11px] text-gray-400 font-abel text-center mb-4">
                      Selected on{" "}
                      {new Date(fotwSetAt).toLocaleDateString(undefined, {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </p>
                  )}

                  <div className="flex-1 space-y-2.5">
                    {(faceOfWeek.dob || faceOfWeek.gender) && (
                      <div className="grid grid-cols-2 gap-3">
                        {faceOfWeek.dob && (
                          <div>
                            <p className="text-xs text-gray-400 font-abel uppercase tracking-wider">
                              Date of Birth
                            </p>
                            <p className="text-sm text-gray-700 font-abel font-semibold mt-0.5">
                              {formatDob(faceOfWeek.dob)}
                            </p>
                          </div>
                        )}
                        {faceOfWeek.gender && (
                          <div>
                            <p className="text-xs text-gray-400 font-abel uppercase tracking-wider">
                              Gender
                            </p>
                            <p className="text-sm text-gray-700 font-abel font-semibold mt-0.5">
                              {faceOfWeek.gender}
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                    {faceOfWeek.department && (
                      <div>
                        <p className="text-xs text-gray-400 font-abel uppercase tracking-wider">
                          Department
                        </p>
                        <p className="text-sm text-gray-700 font-abel font-semibold mt-0.5">
                          {formatDept(faceOfWeek.department)}
                        </p>
                      </div>
                    )}
                    {(faceOfWeek.favBibleVerse || faceOfWeek.favHymns) && (
                      <div className="grid grid-cols-2 gap-3">
                        {faceOfWeek.favBibleVerse && (
                          <div>
                            <p className="text-xs text-gray-400 font-abel uppercase tracking-wider">
                              Fav Bible Verse
                            </p>
                            <p className="text-sm text-gray-700 font-abel font-semibold mt-0.5">
                              {faceOfWeek.favBibleVerse}
                            </p>
                          </div>
                        )}
                        {faceOfWeek.favHymns && (
                          <div>
                            <p className="text-xs text-gray-400 font-abel uppercase tracking-wider">
                              Fav Hymn
                            </p>
                            <p className="text-sm text-gray-700 font-abel font-semibold mt-0.5">
                              {faceOfWeek.favHymns}
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="flex flex-col items-center justify-center flex-1 py-6 text-center">
                  <div className="w-14 h-14 rounded-2xl bg-amber-50 flex items-center justify-center mb-3">
                    <Star style={{ color: "#FFC000", fontSize: 26 }} />
                  </div>
                  <p className="text-gray-400 text-sm font-abel">
                    No one selected yet
                  </p>
                </div>
              )}

              {userRole !== "editor" && userRole !== "operator" && (
                <button
                  onClick={() => {
                    setFotwPage(0);
                    setFotwModalOpen(true);
                  }}
                  className="mt-4 w-full flex items-center justify-center gap-2 bg-[#000C66] text-white px-4 py-2.5 rounded-xl font-abel font-semibold text-sm hover:bg-blue-900 transition"
                >
                  <Star style={{ fontSize: 15 }} />
                  {faceOfWeek ? "Change VOTW" : "Select VOTW"}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Families table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mb-5">
        <div className="p-4 border-b border-gray-100 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Groups style={{ color: "#be185d", fontSize: 18 }} />
            <h2 className="text-sm font-bold text-[#000C66] font-aref">
              Families ({families.length})
            </h2>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {(userRole === "admin" || userRole === "superadmin") && duplicateFamilyGroups.length > 0 && (
              <button
                onClick={() => setMergeModalOpen(true)}
                className="flex items-center gap-1.5 text-xs font-abel font-semibold px-3 py-2 bg-amber-50 text-amber-700 border border-amber-200 rounded-xl hover:bg-amber-100 transition"
              >
                <MergeType style={{ fontSize: 15 }} />
                {duplicateFamilyGroups.length} Duplicate{duplicateFamilyGroups.length > 1 ? "s" : ""} Found
              </button>
            )}
            <div className="relative max-w-xs w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" style={{ fontSize: 18 }} />
              <input
                type="text"
                value={familySearchQuery}
                onChange={(e) => setFamilySearchQuery(e.target.value)}
                placeholder="Search family name…"
                className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-xl text-sm font-abel text-gray-700 focus:outline-none focus:border-[#000C66] focus:ring-2 focus:ring-[#000C66]/10 transition bg-gray-50"
              />
            </div>
          </div>
        </div>
        <CustomTable
          headers={familyHeader}
          data={filteredFamilies}
          extraActions={(row) => (
            <button
              onClick={() => {
                setSelectedFamily(row);
                setFamilyMembersOpen(true);
              }}
              className="flex items-center gap-1 text-xs font-abel font-semibold px-3 py-1.5 border border-gray-200 text-[#000C66] rounded-lg hover:bg-[#eef2ff] transition"
            >
              <Visibility style={{ fontSize: 14 }} /> Preview
            </button>
          )}
        />
      </div>

      {/* FOTW Modal */}
      {fotwModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md flex flex-col"
            style={{ maxHeight: "85vh" }}
          >
            {/* Modal header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
              <div className="flex items-center gap-2">
                <Star style={{ color: "#FFC000", fontSize: 18 }} />
                <h3 className="font-bold text-[#000C66] font-aref text-base">
                  Select Vessel of the Week
                </h3>
              </div>
              <button
                onClick={() => setFotwModalOpen(false)}
                className="w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:bg-gray-100 transition text-xl font-bold leading-none"
              >
                ×
              </button>
            </div>

            {/* Member list */}
            <div className="overflow-y-auto flex-1 py-2">
              {members.length === 0 ? (
                <p className="text-center text-gray-400 text-sm font-abel py-10">
                  No approved members found
                </p>
              ) : (
                pagedMembers.map((m) => {
                  const isCurrent = faceOfWeek?._id === m._id;
                  const recentlySelected = isRecentlySelected(m._id, fotwHistory);
                  const locked = recentlySelected && !isCurrent;

                  return (
                    <div
                      key={m._id}
                      className={`mx-2 px-3 py-3 rounded-xl transition ${isCurrent ? "bg-amber-50" : "hover:bg-gray-50"}`}
                    >
                      <div className="flex items-center gap-3">
                        {/* Avatar */}
                        {m.imgSrc ? (
                          <img
                            src={m.imgSrc}
                            alt={m.name}
                            className="w-11 h-11 rounded-full object-cover border-2 border-gray-100 flex-shrink-0"
                          />
                        ) : (
                          <div className="w-11 h-11 rounded-full bg-[#eef2ff] flex items-center justify-center flex-shrink-0 border-2 border-gray-100">
                            <span className="text-[#000C66] font-bold text-sm font-aref">
                              {m.name?.charAt(0).toUpperCase()}
                            </span>
                          </div>
                        )}

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-800 font-abel truncate">
                            {m.name}
                          </p>
                          <p className="text-xs text-gray-400 font-abel truncate">
                            {[formatDob(m.dob), formatDept(m.department)]
                              .filter(Boolean)
                              .join(" · ")}
                          </p>
                        </div>

                        {/* Action */}
                        {isCurrent ? (
                          <span className="text-xs font-abel font-semibold px-2.5 py-1 bg-amber-100 text-amber-600 rounded-full flex-shrink-0">
                            ⭐ Current
                          </span>
                        ) : locked ? (
                          <span className="flex items-center gap-1 text-xs font-abel font-semibold px-3 py-1.5 bg-gray-100 text-gray-400 rounded-lg flex-shrink-0 cursor-not-allowed">
                            🔒 Locked
                          </span>
                        ) : (
                          <button
                            onClick={() => handleSetFaceOfWeek(m)}
                            disabled={!!fotwLoading}
                            className="flex items-center gap-1 text-xs font-abel font-semibold px-3 py-1.5 bg-[#000C66] text-white rounded-lg hover:bg-blue-900 transition disabled:opacity-50 flex-shrink-0"
                          >
                            {fotwLoading === m._id ? (
                              <span className="w-3 h-3 border border-white/30 border-t-white rounded-full animate-spin" />
                            ) : (
                              "⭐"
                            )}
                            VOTW
                          </button>
                        )}
                      </div>

                      {/* 2-month lock notice */}
                      {locked && (
                        <div className="mt-2 flex items-start gap-1.5 bg-orange-50 border border-orange-200 rounded-lg px-3 py-2">
                          <span className="text-orange-400 text-xs mt-0.5 flex-shrink-0">
                            ⚠️
                          </span>
                          <p className="text-xs text-orange-600 font-abel">
                            {m.name} was selected as Vessel of the Week within
                            the last 2 months and can't be picked again yet.
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100 flex-shrink-0">
                <button
                  onClick={() => setFotwPage((p) => Math.max(0, p - 1))}
                  disabled={fotwPage === 0}
                  className="flex items-center gap-1 text-xs font-abel font-semibold px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronLeft style={{ fontSize: 16 }} /> Prev
                </button>
                <span className="text-xs text-gray-400 font-abel">
                  Page {fotwPage + 1} of {totalPages}
                </span>
                <button
                  onClick={() =>
                    setFotwPage((p) => Math.min(totalPages - 1, p + 1))
                  }
                  disabled={fotwPage >= totalPages - 1}
                  className="flex items-center gap-1 text-xs font-abel font-semibold px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Next <ChevronRight style={{ fontSize: 16 }} />
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Family members preview modal */}
      {familyMembersOpen && selectedFamily && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-sm flex flex-col"
            style={{ maxHeight: "80vh" }}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
              <h3 className="font-bold text-[#000C66] font-aref text-base">
                {selectedFamily.familyName} Family
              </h3>
              <button
                onClick={() => setFamilyMembersOpen(false)}
                className="w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:bg-gray-100 transition text-xl font-bold leading-none"
              >
                ×
              </button>
            </div>

            <div className="overflow-y-auto flex-1 py-2">
              {(selectedFamily.members || []).length === 0 ? (
                <p className="text-center text-gray-400 text-sm font-abel py-10">
                  No members listed
                </p>
              ) : (
                selectedFamily.members.map((m, i) => (
                  <div
                    key={m.id || i}
                    className="mx-2 px-3 py-2.5 rounded-xl hover:bg-gray-50 transition flex items-center gap-3"
                  >
                    <div className="w-9 h-9 rounded-full bg-[#eef2ff] flex items-center justify-center flex-shrink-0">
                      <span className="text-[#000C66] font-bold text-xs font-aref">
                        {m.name?.charAt(0)?.toUpperCase() || "?"}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-800 font-abel truncate">
                        {m.name}
                      </p>
                      <p className="text-xs text-gray-400 font-abel truncate">
                        {m.email || "No email"}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Merge duplicate families modal */}
      {mergeModalOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col"
            style={{ maxHeight: "85vh" }}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
              <div className="flex items-center gap-2">
                <MergeType style={{ color: "#000C66", fontSize: 18 }} />
                <h3 className="font-bold text-[#000C66] font-aref text-base">Duplicate Families</h3>
              </div>
              <button
                onClick={() => setMergeModalOpen(false)}
                className="w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:bg-gray-100 transition text-xl font-bold leading-none"
              >
                ×
              </button>
            </div>

            <div className="overflow-y-auto flex-1 p-4 space-y-4">
              {duplicateFamilyGroups.length === 0 ? (
                <p className="text-center text-gray-400 text-sm font-abel py-6">
                  No duplicate families found.
                </p>
              ) : (
                duplicateFamilyGroups.map((group) => {
                  const sorted = sortForMerge(group);
                  const survivor = sorted[0];
                  const toMerge = sorted.slice(1);
                  const groupKey = (survivor.familyName || "").trim().toLowerCase();
                  const isMerging = mergingGroupKey === groupKey;

                  return (
                    <div key={groupKey} className="border border-gray-100 rounded-xl p-3">
                      <p className="text-sm font-bold text-gray-800 font-abel mb-2">
                        {survivor.familyName} Family — {group.length} records found
                      </p>
                      <div className="space-y-1 mb-3">
                        {sorted.map((f) => (
                          <div
                            key={f._id}
                            className="flex items-center justify-between text-xs font-abel text-gray-500"
                          >
                            <span>
                              {(f.members || []).length} member
                              {(f.members || []).length !== 1 ? "s" : ""}
                            </span>
                            <span
                              className={
                                f._id === survivor._id
                                  ? "text-green-600 font-semibold"
                                  : "text-orange-500"
                              }
                            >
                              {f._id === survivor._id ? "Keep" : "Will merge in"}
                            </span>
                          </div>
                        ))}
                      </div>
                      <button
                        onClick={() => handleMergeGroup(group)}
                        disabled={isMerging}
                        className="w-full flex items-center justify-center gap-2 bg-[#000C66] text-white px-3 py-2 rounded-lg font-abel font-semibold text-xs hover:bg-blue-900 transition disabled:opacity-60"
                      >
                        {isMerging && (
                          <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        )}
                        Merge {toMerge.length} record{toMerge.length !== 1 ? "s" : ""} into the {(survivor.members || []).length}-member family
                      </button>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;
